import React, { useEffect, Suspense, lazy } from "react";

import { Route, Switch } from "react-router-dom";
import { Provider, useDispatch } from "react-redux";
import store from "utils/store";
import history from "utils/history";
import { ConnectedRouter } from "connected-react-router";
import DocumentTitle from "react-document-title";
import appActions from "actions/appActions";
import { GoogleTagManager } from "utils/GoogleTagManager";
import "react-step-progress-bar/styles.css";
import PageLoader from "./components/PageLoader";

// array of routes
import routes from "./routes";
import HttpErrorBoundary from "components/common/HttpErrorBoundary";
import ErrorBoundary from "./ErrorBoundary";
import { SocketWrapper } from "./SocketContext";
import SocketEvents from "./SocketEvents";
import Login from "components/Auth/Login";
import Register from "components/Auth/Register";
import { AuthSignInCallbackRoute, AuthSignOutCallbackRoute } from "auth";
import { RecoilRoot } from "recoil";

// Components
const Index = lazy(() => import("./components/Index"));
const NotFound = lazy(() => import("./NotFound"));

// having throttling issues
history.listen((location, action) => {
	if (action === "POP") {
		return;
	}
});

const App = () => {
	return (
		<RecoilRoot>
			<DocumentTitle title="Vatom Studio™">
				<Provider store={store}>
					<ConnectedRouter history={history}>
						<ErrorBoundary>
							<HttpErrorBoundary>
								<SocketWrapper>
									<GetSystemInfo />
									<SocketEvents />

									<GoogleTagManager gtmId={process.env.REACT_APP_GTM_APP_ID!} />

									<Suspense fallback={<PageLoader />}>
										<Switch>
											<Route exact path="/" component={Index} />

											<Route exact path="/login" component={Login} />
											<Route exact path="/register" component={Register} />

											<Route exact path="/callback">
												<AuthSignInCallbackRoute>
													<PageLoader />
												</AuthSignInCallbackRoute>
											</Route>

											<Route exact path="/logout-callback">
												<AuthSignOutCallbackRoute>
													<PageLoader />
												</AuthSignOutCallbackRoute>
											</Route>

											{routes.map(({ path, exact, component }, key) => (
												<Route key={key} path={path} exact={exact} component={component} />
											))}

											<Route component={NotFound} />
										</Switch>
									</Suspense>
								</SocketWrapper>
							</HttpErrorBoundary>
						</ErrorBoundary>
					</ConnectedRouter>
				</Provider>
			</DocumentTitle>
		</RecoilRoot>
	);
};

const GetSystemInfo = () => {
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(appActions.getSystemInfo());
	}, [dispatch]);

	return null;
};

export default App;
